import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, ExternalLink, BookOpen, Calendar, Users, Award,
  Bookmark, Check, ChevronDown,
} from 'lucide-react'
import { getJournal, getPersonalLists, addListItem } from '../../services/api'
import { stripJatsXml } from '../../utils/stripJatsXml'
import { formatTopicLabel } from '../../utils/topics'
import { parseStructuredAbstract } from '../../utils/parseStructuredAbstract'
import type { PersonalList } from '../../types'
import InlineConnectionsPanel from '../../components/graph/InlineConnectionsPanel'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import MathText from '../../components/shared/MathText'
import PageHeader from '../../components/ui/PageHeader'
import EmptyState from '../../components/ui/EmptyState'

function AddToListButton({ journalId, journalTitle }: { journalId: string; journalTitle: string }) {
  const [open, setOpen] = useState(false)
  const [added, setAdded] = useState<string | null>(null)
  const [addError, setAddError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const { data: lists } = useQuery({
    queryKey: ['personal-lists'],
    queryFn: getPersonalLists,
    enabled: open,
  })

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleAdd = async (list: PersonalList) => {
    setAddError(null)
    try {
      await addListItem(list.list_id, {
        resource_id: journalId,
        resource_type: 'journal',
        title: journalTitle,
      })
      setAdded(list.list_id)
      queryClient.invalidateQueries({ queryKey: ['list-items', list.list_id] })
      setTimeout(() => { setAdded(null); setOpen(false) }, 1200)
    } catch (err) {
      // BUG-127: only a 409 means "already in this list" — a benign duplicate we
      // can treat as success. Every other outcome (403/500/network) is a real
      // failure and must be surfaced instead of silently swallowed.
      const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string }
      if (e?.response?.status === 409) {
        setAdded(list.list_id)
        setTimeout(() => { setAdded(null); setOpen(false) }, 1200)
      } else {
        setAddError(e?.response?.data?.error || e?.message || 'Failed to add to list')
      }
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-sky-200 rounded-lg hover:bg-white/50 transition-colors"
      >
        <Bookmark size={14} />
        Add to List
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 light-card shadow-lg z-20 py-1">
          {!lists || lists.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-500">No lists yet. Create one from Lists page.</p>
          ) : (
            lists.map((l) => (
              <button
                key={l.list_id}
                onClick={() => handleAdd(l)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-white/50"
              >
                <span className="truncate">{l.name}</span>
                {added === l.list_id && <Check size={14} className="text-green-500 flex-shrink-0" />}
              </button>
            ))
          )}
          {addError && (
            <p data-testid="add-list-error" className="px-3 py-2 text-xs text-red-600 border-t border-sky-200/50">{addError}</p>
          )}
        </div>
      )}
    </div>
  )
}

export default function JournalDetail() {
  const params = useParams()
  // Journal IDs are DOIs with slashes (e.g. 10.1007/s10796-021-10199-5)
  // so we use a wildcard route and extract the full path via '*'
  // Links encode the id (an openalex id carries a whole URL with '//'), so decode it back; a plain
  // id with no '%' passes through unchanged.
  const rawJournalId = params['*']
  let journalId = rawJournalId
  try { journalId = rawJournalId ? decodeURIComponent(rawJournalId) : rawJournalId } catch { journalId = rawJournalId }
  const navigate = useNavigate()

  const { data: journal, isLoading, error } = useQuery({
    queryKey: ['journal', journalId],
    queryFn: () => getJournal(journalId!),
    enabled: !!journalId,
  })

  if (isLoading) return <LoadingSkeleton rows={6} type="list" />

  if (error || !journal) {
    return (
      <div className="max-w-4xl mx-auto">
        <EmptyState
          icon={BookOpen}
          title="Journal not found"
          description="We couldn't load this paper. It may have been removed from the index, or the link may be incorrect."
          action={
            <button
              onClick={() => navigate('/journals')}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800"
            >
              <ArrowLeft size={14} /> Back to Journals
            </button>
          }
        />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back nav */}
      <button
        onClick={() => navigate('/journals')}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft size={14} /> Back to Journals
      </button>

      <PageHeader
        title={journal.title}
        helpKey="journals"
        primaryAction={<AddToListButton journalId={journal._id} journalTitle={journal.title} />}
      />

      {/* Metadata card */}
      <div className="light-card p-6 mb-6">
        {/* Authors */}
        {journal.authors && journal.authors.length > 0 && (
          <div className="flex items-center gap-2 mt-3 text-sm text-gray-600">
            <Users size={14} className="text-gray-500 flex-shrink-0" />
            <span>{journal.authors.join(', ')}</span>
          </div>
        )}

        {/* Publication info row */}
        <div className="flex flex-wrap items-center gap-4 mt-3">
          {journal.journal_name && (
            <span className="text-sm text-gray-600 italic">{journal.journal_name}</span>
          )}
          {journal.publication_date && (
            <span className="flex items-center gap-1 text-sm text-gray-500">
              <Calendar size={13} />
              {journal.publication_date}
            </span>
          )}
          {journal.citation_count != null && journal.citation_count > 0 && (
            <span className="flex items-center gap-1 text-sm text-gray-500">
              <Award size={13} />
              {journal.citation_count.toLocaleString()} citations
            </span>
          )}
        </div>

        {/* Badges */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {journal.peer_reviewed && (
            <span className="px-2.5 py-1 text-xs font-medium bg-green-500/20 text-green-700 rounded-full">
              Peer Reviewed
            </span>
          )}
          {journal.open_access && (
            <span className="px-2.5 py-1 text-xs font-medium bg-iflow-50 text-iflow-700 rounded-full">
              Open Access
            </span>
          )}
          {journal.resource_type && (
            <span className="px-2.5 py-1 text-xs font-medium bg-sky-500/10 text-gray-600 rounded-full">
              {journal.resource_type}
            </span>
          )}
        </div>

        {/* DOI */}
        {journal.doi && (
          <a
            href={`https://doi.org/${journal.doi}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-3 text-sm text-iflow-600 hover:text-iflow-700"
          >
            <ExternalLink size={13} />
            DOI: {journal.doi}
          </a>
        )}

        {/* Abstract */}
        {journal.abstract && (() => {
          const parsed = parseStructuredAbstract(stripJatsXml(journal.abstract))
          const isStructured = parsed.sections.length > 1 || parsed.sections[0]?.heading !== ''
          return (
            <div className="mt-4 pt-4 border-t border-sky-200/50">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Abstract</h3>
              {isStructured ? (
                <div className="space-y-2">
                  {parsed.sections.map((sec, i) => (
                    <div key={i}>
                      {sec.heading && (
                        <span className="text-xs font-semibold uppercase tracking-wide text-iflow-600 mr-1.5">
                          {sec.heading}
                        </span>
                      )}
                      <span className="text-sm text-gray-600 leading-relaxed">
                        {parsed.hasMath ? <MathText>{sec.content}</MathText> : sec.content}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-600 leading-relaxed">
                  {parsed.hasMath
                    ? <MathText>{parsed.sections[0]?.content ?? ''}</MathText>
                    : parsed.sections[0]?.content}
                </p>
              )}
            </div>
          )
        })()}

        {/* Topics */}
        {journal.topics && journal.topics.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-sky-200/50">
            {journal.topics.slice(0, 8).map((t) => (
              <span key={t} className="px-2 py-0.5 bg-sky-500/10 text-gray-600 text-xs rounded-full">
                {formatTopicLabel(t)}
              </span>
            ))}
            {journal.topics.length > 8 && (
              <span className="text-[10px] text-gray-500">+{journal.topics.length - 8} more</span>
            )}
          </div>
        )}
      </div>

      {/* Connections panel */}
      <InlineConnectionsPanel resourceId={journal._id} title="Journal Connections" />
    </div>
  )
}
